import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, Building2, ExternalLink } from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { DateCell } from "@/components/admin/DateCell";
import { adminListBusinesses } from "@/lib/business.functions";
import { formatOrgNumber } from "@/lib/business";

export const Route = createFileRoute("/_authenticated/admin/foretag")({
  head: () => ({
    meta: [
      { title: "Företag — Administration | Sellavo" },
      {
        name: "description",
        content: "Alla företagsprofiler: verifiering, organisationsnummer och publika sidor.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <AdminShell title="Företag">
      <BusinessAdmin />
    </AdminShell>
  ),
});

function statusLabel(status: string | null): { text: string; cls: string } {
  if (status === "verified") return { text: "Verifierad", cls: "bg-primary/15 text-primary" };
  if (status === "failed") return { text: "Ej godkänd", cls: "bg-destructive/15 text-destructive" };
  return { text: "Obekräftad", cls: "bg-muted text-muted-foreground" };
}

function BusinessAdmin() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-businesses"],
    queryFn: () => adminListBusinesses(),
  });

  const rows = data ?? [];
  const verified = rows.filter((r: any) => r.verification_status === "verified").length;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Företagsprofiler" value={rows.length} />
        <Stat label="Verifierade" value={verified} />
        <Stat label="Väntar" value={rows.length - verified} />
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Företag</th>
              <th className="px-4 py-3">Org.nr</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Skapad</th>
              <th className="px-4 py-3">Sida</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  Hämtar företag…
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  Inga företagsprofiler än.
                </td>
              </tr>
            )}
            {rows.map((b: any) => {
              const s = statusLabel(b.verification_status);
              return (
                <tr key={b.user_id} className="border-t border-border">
                  <td className="px-4 py-3">
                    <Link
                      to="/admin/anvandare/$userId"
                      params={{ userId: b.user_id }}
                      className="inline-flex items-center gap-2 font-medium hover:text-primary"
                    >
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      {b.company_name ?? "—"}
                    </Link>
                    {b.verified_name && b.verified_name !== b.company_name && (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Bolagsverket: {b.verified_name}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 tabular-nums">
                    {b.org_number ? formatOrgNumber(b.org_number) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${s.cls}`}
                    >
                      {b.verification_status === "verified" && <BadgeCheck className="h-3.5 w-3.5" />}
                      {s.text}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <DateCell value={b.created_at} />
                  </td>
                  <td className="px-4 py-3">
                    {b.slug ? (
                      <Link
                        to="/foretag/$slug"
                        params={{ slug: b.slug }}
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        Öppna <ExternalLink className="h-3.5 w-3.5" />
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}
